import type { InstallPreferences } from '@/application/ports/InstallPreferences';

const VISITS_KEY = 'ceramica.visits';
const DISMISSED_KEY = 'ceramica.install-dismissed';

/**
 * The install prompt's memory, in localStorage.
 *
 * Every access is guarded. Private browsing and some managed-device policies
 * make `localStorage` throw on read as well as on write, and not being able to
 * count visits is never a reason to break the shell around it. When storage is
 * unavailable the counter reads zero forever, which means the prompt is simply
 * never offered: the right way to fail for a feature that is a convenience.
 */
export class LocalInstallPreferences implements InstallPreferences {
  visits(): number {
    return this.read(VISITS_KEY);
  }

  recordVisit(): void {
    this.write(VISITS_KEY, String(this.read(VISITS_KEY) + 1));
  }

  dismissed(): boolean {
    return this.read(DISMISSED_KEY) > 0;
  }

  dismiss(): void {
    this.write(DISMISSED_KEY, '1');
  }

  private read(key: string): number {
    try {
      const raw = localStorage.getItem(key);
      const value = Number(raw ?? '0');
      return Number.isFinite(value) ? value : 0;
    } catch {
      return 0;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* see the note on the class */
    }
  }
}
